import { useMemo, useState } from "react";
import {
    AxnuiErrorCodeTypes,
    PolicyDecisionResultTypes
} from "@iddataweb/idw-common-ui/enums";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { getMatchesCustomRedirectUrl, getRedirectUrlWithoutHostname } from "@iddataweb/idw-common-ui/utils";
import { AXNUIThemingTabTypes } from "@enums/axnui-theming-tab-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { AXNUIThemingPreviewConfigurationState } from "@idw-types/common/axnui/axnui-theming-preview-configuration-state";
import { ERROR_CODE_NONE_LABEL } from "@utils/content-util";
import { ToggleFormGroup } from "@containers/common/toggle-form-group/toggle-form-group";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import styles from "./axnui-theming-preview-configuration-container.less";

type AXNUIThemingPreviewConfigurationContainerProps = {
    currentTab: AXNUIThemingTabTypes;
    redirectUrls: Array<string>;
    previewConfigurationState: AXNUIThemingPreviewConfigurationState;
    setPreviewConfigurationState: (previewConfigurationState: AXNUIThemingPreviewConfigurationState) => void;
};

export const AXNUIThemingPreviewConfigurationContainer = (props: AXNUIThemingPreviewConfigurationContainerProps) => {
    const {
        currentTab,
        redirectUrls,
        previewConfigurationState,
        setPreviewConfigurationState
    } = props;

    const [labelWidth] = useState(LabelWidthTypes.EXTRA_SMALL);

    const handleShowAsyncButtonChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showAsyncButton: !previewConfigurationState.showAsyncButton
        });
    };

    const handleShowAfterCapturePreviewChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showAfterCapturePreview: !previewConfigurationState.showAfterCapturePreview
        });
    };

    const handleShowAsyncModalChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showAsyncModal: !previewConfigurationState.showAsyncModal
        });
    };

    const handleShowAsyncSubmissionInProgressChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showAsyncSubmissionInProgress: !previewConfigurationState.showAsyncSubmissionInProgress
        });
    };


    const handleShowContinueInfoEmailChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showContinueInfoEmail: !previewConfigurationState.showContinueInfoEmail
        });
    };

    const handleShowNoRetriesLeftChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showNoRetriesLeft: !previewConfigurationState.showNoRetriesLeft
        });
    };

    const handleErrorCodeTypesChange = (option: SelectOption<AxnuiErrorCodeTypes>) => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            selectedErrorCode: option.value
        });
    };

    const handleShowReferenceIDLabelChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showErrorPageReferenceId: !previewConfigurationState.showErrorPageReferenceId
        });
    };

    const handleLandingPageChange = (option: SelectOption) => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            selectedLandingPage: option.value
        });
    };

    const handleLandingPagePolicyDecisionTypeChange = (option: SelectOption<PolicyDecisionResultTypes>) => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            selectedLandingPagePolicyDecisionResultType: option.value
        });
    };

    const handleShowMobileCompleteErrorMessageChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showMobileCompleteErrorMessage: !previewConfigurationState.showMobileCompleteErrorMessage
        });
    };

    const handleShowKbaRetryMessageChange = () => {
        setPreviewConfigurationState({
            ...previewConfigurationState,
            showKbaRetryMessage: !previewConfigurationState.showKbaRetryMessage
        });
    };

    const ErrorTypeCodes = useMemo(() => {
        const errorCodes = Object.keys(AxnuiErrorCodeTypes).map(key => {
            return { value: key, label: key }
        });

        errorCodes.unshift({ label: ERROR_CODE_NONE_LABEL, value: null });
        return errorCodes;
    }, []);

    const selectedErrorCodeOption: SelectOption<AxnuiErrorCodeTypes> = useMemo(() => {
        const { selectedErrorCode } = previewConfigurationState;
        return { label: selectedErrorCode ? selectedErrorCode : ERROR_CODE_NONE_LABEL, value: selectedErrorCode };
    }, [previewConfigurationState.selectedErrorCode]);

    const landingPageOptions: Array<SelectOption> = useMemo(() => {
        const formattedRedirectUrls: Array<SelectOption> = [];
        (redirectUrls || []).forEach(redirectUrl => {
            const redirectUrlWithoutHostname = getRedirectUrlWithoutHostname(redirectUrl);
            formattedRedirectUrls.push({ label: redirectUrlWithoutHostname, value: redirectUrl });
        });
        return formattedRedirectUrls;
    }, [redirectUrls]);

    const selectedLandingPageOption: SelectOption = useMemo(() => {
        return landingPageOptions.find(lpo => lpo.value === previewConfigurationState.selectedLandingPage) || null;
    }, [previewConfigurationState.selectedLandingPage, landingPageOptions]);

    const landingPagePolicyDecisionTypeOptions: Array<SelectOption<PolicyDecisionResultTypes>> = useMemo(() => {
        return [
            { label: PolicyDecisionResultTypes.APPROVE, value: PolicyDecisionResultTypes.APPROVE },
            { label: PolicyDecisionResultTypes.DENY, value: PolicyDecisionResultTypes.DENY }
        ]
    }, []);

    const selectedLandingPagePolicyDecisionTypeOption = useMemo(() => {
        return landingPagePolicyDecisionTypeOptions.find(lpmto => lpmto.value === previewConfigurationState.selectedLandingPagePolicyDecisionResultType);
    }, [
        landingPagePolicyDecisionTypeOptions,
        previewConfigurationState.selectedLandingPagePolicyDecisionResultType
    ]);

    const showCustomLandingPage = useMemo(() => {
        if (previewConfigurationState.selectedLandingPage) {
            return getMatchesCustomRedirectUrl(previewConfigurationState.selectedLandingPage);
        }
        return false;
    }, [previewConfigurationState.selectedLandingPage]);

    return (
        <div className={styles.axnuiThemingPreviewConfigurationContainer}>
            {currentTab === AXNUIThemingTabTypes.ACTION_BUTTON && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - Action Button</span>
                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-async-button-toggle"}
                            automationId={"show-async-button-toggle"}
                            label={"Show 'Continue on Mobile' Button"}
                            ariaLabel={"Show 'Continue on Mobile' Button"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={labelWidth}
                            checked={previewConfigurationState.showAsyncButton}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowAsyncButtonChange}
                            value={`${previewConfigurationState.showAsyncButton}`}
                        />
                    </div>
                </>
            )}
            {currentTab === AXNUIThemingTabTypes.FORM_CONTENT && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - Form Content</span>
                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-after-capturing-toggle"}
                            automationId={"show-after-capturing-toggle"}
                            label={"Show After Capture Preview"}
                            ariaLabel={"Show After Capture Preview"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={labelWidth}
                            checked={previewConfigurationState.showAfterCapturePreview}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowAfterCapturePreviewChange}
                            value={`${previewConfigurationState.showAfterCapturePreview}`}
                        />
                    </div>
                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-async-modal-toggle"}
                            automationId={"show-async-modal-toggle"}
                            label={"Show Async Modal"}
                            ariaLabel={"Show Async Modal"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={labelWidth}
                            checked={previewConfigurationState.showAsyncModal}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowAsyncModalChange}
                            value={`${previewConfigurationState.showAsyncModal}`}
                        />
                    </div>
                    {previewConfigurationState.showAsyncModal && (
                        <>
                            <span className={styles.previewConfigurationTitle}>Preview Configuration - Async Modal</span>
                            <div className={styles.toggleContainer}>
                                <ToggleFormGroup
                                    controlId={"show-continue-info-email-toggle"}
                                    automationId={"show-continue-info-email-toggle"}
                                    label={"Show Continue Info Email Text"}
                                    ariaLabel={"Show Continue Info Email Text"}
                                    checkedIcon={false}
                                    uncheckedIcon={false}
                                    labelWidth={labelWidth}
                                    checked={previewConfigurationState.showContinueInfoEmail}
                                    disabled={false}
                                    isRequired={false}
                                    onChange={handleShowContinueInfoEmailChange}
                                    value={`${previewConfigurationState.showContinueInfoEmail}`}
                                />
                            </div>
                            <div className={styles.toggleContainer}>
                                <ToggleFormGroup
                                    controlId={"show-async-submission-in-progress-toggle"}
                                    automationId={"show-async-submission-in-progress-toggle"}
                                    label={"Show In Progress Text"}
                                    ariaLabel={"Show In Progress Text"}
                                    checkedIcon={false}
                                    uncheckedIcon={false}
                                    labelWidth={labelWidth}
                                    checked={previewConfigurationState.showAsyncSubmissionInProgress}
                                    disabled={false}
                                    isRequired={false}
                                    onChange={handleShowAsyncSubmissionInProgressChange}
                                    value={`${previewConfigurationState.showAsyncSubmissionInProgress}`}
                                />
                            </div>
                            <div className={styles.toggleContainer}>
                                <ToggleFormGroup
                                    controlId={"show-no-retries-left-toggle"}
                                    automationId={"show-no-retries-left-toggle"}
                                    label={"Show No Retries Left Text"}
                                    ariaLabel={"Show No Retries Left Text"}
                                    checkedIcon={false}
                                    uncheckedIcon={false}
                                    labelWidth={labelWidth}
                                    checked={previewConfigurationState.showNoRetriesLeft}
                                    disabled={false}
                                    isRequired={false}
                                    onChange={handleShowNoRetriesLeftChange}
                                    value={`${previewConfigurationState.showNoRetriesLeft}`}
                                />
                            </div>
                        </>
                    )}
                </>
            )}
            {currentTab === AXNUIThemingTabTypes.ERROR_PAGE && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - Error Page</span>
                    <div className={styles.toggleContainer}>
                        <SelectFormGroup
                            controlId={"error-code-type-select"}
                            automationId={"error-code-type-select"}
                            label={"Error Code Types"}
                            ariaLabel={"Error Code Types"}
                            options={ErrorTypeCodes}
                            selectedOption={selectedErrorCodeOption}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            isClearable={false}
                            isSearchable={false}
                            disabled={false}
                            isRequired={false}
                            onChange={handleErrorCodeTypesChange}

                        />

                    </div>

                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-reference-id-toggle"}
                            automationId={"show-reference-id-toggle"}
                            label={"Show Reference ID Label"}
                            ariaLabel={"Show Reference ID Label"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            checked={previewConfigurationState.showErrorPageReferenceId}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowReferenceIDLabelChange}
                            value={`${previewConfigurationState.showErrorPageReferenceId}`}
                        />
                    </div>
                </>
            )}
            {currentTab === AXNUIThemingTabTypes.LANDING_PAGE && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - Landing Page</span>
                    <div className={styles.toggleContainer}>
                        <SelectFormGroup
                            controlId={"landing-page-select"}
                            automationId={"landing-page-select"}
                            label={"Landing Page"}
                            ariaLabel={"Landing Page"}
                            options={landingPageOptions}
                            selectedOption={selectedLandingPageOption}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            isClearable={false}
                            isSearchable={false}
                            disabled={false}
                            isRequired={false}
                            onChange={handleLandingPageChange}
                        />
                    </div>
                    {showCustomLandingPage && (
                        <div className={styles.toggleContainer}>
                            <SelectFormGroup
                                controlId={"landing-page-policy-decision-type-select"}
                                automationId={"landing-page-policy-decision-type-select"}
                                label={"Policy Decision"}
                                ariaLabel={"Policy Decision"}
                                options={landingPagePolicyDecisionTypeOptions}
                                selectedOption={selectedLandingPagePolicyDecisionTypeOption}
                                labelWidth={LabelWidthTypes.MEDIUM}
                                isClearable={false}
                                isSearchable={false}
                                disabled={false}
                                isRequired={false}
                                onChange={handleLandingPagePolicyDecisionTypeChange}
                            />
                        </div>
                    )}
                </>
            )}
            {currentTab === AXNUIThemingTabTypes.MOBILE_COMPLETE_PAGE && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - Mobile Complete Page</span>
                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-mobile-complete-error-message-toggle"}
                            automationId={"show-mobile-complete-error-message-toggle"}
                            label={"Show Error Message"}
                            ariaLabel={"Show Error Message"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            checked={previewConfigurationState.showMobileCompleteErrorMessage}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowMobileCompleteErrorMessageChange}
                            value={`${previewConfigurationState.showMobileCompleteErrorMessage}`}
                        />
                    </div>
                </>
            )}
            {currentTab === AXNUIThemingTabTypes.KBA_FORM && (
                <>
                    <span className={styles.previewConfigurationTitle}>Preview Configuration - KBA Form</span>
                    <div className={styles.toggleContainer}>
                        <ToggleFormGroup
                            controlId={"show-kba-form-retry-message-toggle"}
                            automationId={"show-kba-form-retry-message-toggle"}
                            label={"Show KBA Retry Message"}
                            ariaLabel={"Show KBA Retry Message"}
                            checkedIcon={false}
                            uncheckedIcon={false}
                            labelWidth={LabelWidthTypes.MEDIUM}
                            checked={previewConfigurationState.showKbaRetryMessage}
                            disabled={false}
                            isRequired={false}
                            onChange={handleShowKbaRetryMessageChange}
                            value={`${previewConfigurationState.showKbaRetryMessage}`}
                        />
                    </div>
                </>
            )}
        </div>
    );
};